import { useEffect, useState } from "react";

import {
    json,
    redirect,
    type ActionFunctionArgs,
    type LoaderFunction,
} from "@remix-run/node";
import {
    Link,
    useLocation,
    useSearchParams,
    useFetcher,
} from "@remix-run/react";
import { z } from "zod";

import Layout from "~/components/layout";
import { useOnboarding } from "~/components/onboarding-wrapper";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { P } from "~/components/ui/typography";
import { withCors } from "~/config/cors";

// Updated signup logic dependencies
import { getUnauditableSystemAdminSupabaseClient } from "~/integrations/supabase";
import { createSupabaseServerClient } from "~/integrations/supabase/ssr.server";
import { trackEvent } from "~/lib/analytics";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { trackEvent as trackServerEvent } from "~/modules.server/analytics";
import { getLogger } from "~/modules.server/logger";
import { profileRepository } from "~/modules.server/profile/repository";
import { pageMeta } from "~/utils/meta";
import { SERVER_URL } from "~/lib/env.shared";
import { assertIsPost } from "~/utils.server/http.server";

export const meta = pageMeta(
    `Sign Up - ${PRODUCT_NAME}`,
    `Create your ${PRODUCT_NAME} account`,
);

export const loader: LoaderFunction = withCors(async () => json({}));

// Validation schema matching api.auth.signup.ts
const signupSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
    password: z
        .string()
        .min(8, "Password must be at least 8 characters")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/[a-z]/, "Password must contain at least one lowercase letter")
        .regex(/[0-9]/, "Password must contain at least one number"),
});

export const action = withCors(async ({ request }: ActionFunctionArgs) => {
    assertIsPost(request);

    const formData = await request.formData();

    const email = (formData.get("email") || "") as string;
    const password = (formData.get("password") || "") as string;
    const confirmPassword = (formData.get("confirmPassword") || "") as string;
    const redirectTo = formData.get("redirect") as string | null;
    const practitionerInviteToken = formData.get("practitionerInviteToken") as
        | string
        | null;

    // Validate passwords match
    if (password !== confirmPassword) {
        return json({ error: "Passwords don't match" }, { status: 400 });
    }

    // Zod validation for email & password complexity
    const validationResult = signupSchema.safeParse({ email, password });
    if (!validationResult.success) {
        return json(
            {
                error:
                    validationResult.error.errors[0]?.message ||
                    "Validation failed",
            },
            { status: 400 },
        );
    }

    // Determine origin for email redirect
    const origin = request.headers.get("Origin") || SERVER_URL;

    try {
        // Use SSR server client for signup
        const { supabase: ssrSupabase, headers } =
            createSupabaseServerClient(request);

        // Use admin client for profile checks
        const adminSupabase = getUnauditableSystemAdminSupabaseClient();

        // Check for existing profile in our "profiles" table
        const existingProfiles = await profileRepository.findProfilesByEmails({
            emails: [email],
            supabaseClient: adminSupabase,
        });

        if (existingProfiles.length > 0) {
            const practitionerClientLink =
                existingProfiles[0].practitioner_client;

            if (
                existingProfiles[0].onboarding_status !== "completed" &&
                practitionerClientLink
            ) {
                return json(
                    {
                        error: "It looks like your practitioner already created an account for this email.",
                        redirectTo:
                            "/reset-password?message=" +
                            encodeURIComponent(
                                "It looks like your practitioner already created an account for this email. Enter your email to set a password.",
                            ),
                    },
                    { status: 400, headers },
                );
            }

            return json(
                { error: "An account with this email already exists." },
                { status: 400, headers },
            );
        }

        // Check Supabase auth.users directly for existing user
        const { data: existingUsers } =
            await adminSupabase.auth.admin.listUsers();
        const userExists = existingUsers?.users?.some(
            (user) => user.email === email,
        );

        if (userExists) {
            return json(
                { error: "An account with this email already exists." },
                { status: 400, headers },
            );
        }

        getLogger()
            .withMetadata({ email, redirectTo: `${origin}/onboarding/terms` })
            .info("Creating user account");

        // Build redirect URL with practitioner invite token if present
        let emailRedirectTo = `${origin}/auth/callback?next=/onboarding/terms`;
        if (practitionerInviteToken) {
            emailRedirectTo += `&practitionerInviteToken=${encodeURIComponent(
                practitionerInviteToken,
            )}`;
        }

        // Create the user account & trigger email confirmation using SSR client
        const { error, data } = await ssrSupabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo,
            },
        });

        if (error) {
            getLogger().withError(error).error("Signup error");
            return json({ error: error.message }, { status: 400, headers });
        } else {
            getLogger().withMetadata(data).info("User created successfully");
        }

        // AIDEV-NOTE: Log user data for debugging Mixpanel tracking
        getLogger()
            .withMetadata({
                userId: data.user?.id,
                email: data.user?.email,
                hasUser: !!data.user,
                hasSession: !!data.session,
            })
            .info("Signup successful - tracking event");

        trackServerEvent("signed up", {
            source: "signup",
            userId: data.user?.id || null,
        });

        // Return success response instead of redirect when using fetcher
        return json({ success: true, email }, { headers });
    } catch (error) {
        getLogger()
            .withError(error as Error)
            .error("Unexpected signup error");
        return json(
            { error: "An unexpected error occurred. Please try again." },
            { status: 500 },
        );
    }
});

export default function SignupRoute() {
    const fetcher = useFetcher<{
        error?: string;
        success?: boolean;
        email?: string;
        redirectTo?: string;
    }>();
    const { onStepComplete } = useOnboarding();
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get("redirect");
    const practitionerInviteToken = searchParams.get("practitionerInviteToken");
    const location = useLocation();

    const isSubmitting = fetcher.state === "submitting";

    // Handle successful signup
    useEffect(() => {
        if (fetcher.data?.success && fetcher.data.email) {
            // Track event and complete onboarding step
            onStepComplete("signup");

            // Navigate to email confirmation
            const confirmUrl =
                `/email-confirmation?email=${encodeURIComponent(
                    fetcher.data.email,
                )}` +
                (redirect ? `&redirect=${encodeURIComponent(redirect)}` : "") +
                (practitionerInviteToken
                    ? `&practitionerInviteToken=${encodeURIComponent(
                          practitionerInviteToken,
                      )}`
                    : "");

            window.location.href = confirmUrl;
        } else if (fetcher.data?.redirectTo) {
            // Handle redirect for existing practitioner-created accounts
            window.location.href = fetcher.data.redirectTo;
        }
    }, [fetcher.data, onStepComplete, redirect, practitionerInviteToken]);

    return (
        <Layout
            backNavOverride="/landing"
            title="Create an account"
            offsetTop={false}
            narrow={true}
        >
            <div className="mx-auto w-full max-w-sm space-y-6">
                <fetcher.Form
                    method="POST"
                    action="/signup"
                    className="space-y-4"
                >
                    <div>
                        <Input
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            required
                        />
                    </div>
                    <div>
                        <Input
                            name="password"
                            type="password"
                            placeholder="Create a password"
                            required
                        />
                    </div>
                    <div>
                        <Input
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm your password"
                            required
                        />
                    </div>

                    {/* Hidden fields for redirect params */}
                    {redirect && (
                        <input type="hidden" name="redirect" value={redirect} />
                    )}
                    {practitionerInviteToken && (
                        <input
                            type="hidden"
                            name="practitionerInviteToken"
                            value={practitionerInviteToken}
                        />
                    )}

                    {fetcher.data?.error && (
                        <P className="text-destructive text-sm">
                            {fetcher.data.error}
                            {fetcher.data.error ===
                                "User already registered" && (
                                <>
                                    .
                                    <Link
                                        className="ml-2 text-rose-800 underline hover:underline"
                                        to={"/login" + location.search}
                                    >
                                        Sign in instead
                                    </Link>
                                </>
                            )}
                        </P>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        isLoading={isSubmitting}
                    >
                        Create account
                    </Button>
                    <Link
                        to={"/login" + location.search}
                        className="mt-8 block hover:underline"
                    >
                        <Button variant="ghost" className="w-full">
                            Already have an account? Sign in
                        </Button>
                    </Link>
                </fetcher.Form>
            </div>
        </Layout>
    );
}
